import { createClient, type CallOptions, type Interceptor } from "@connectrpc/connect";
import { createConnectTransport } from "@connectrpc/connect-web";

import { getPrincipal } from "@/principal";
import { BuildVersion } from "@/utils/version";
import webConfig from "@/web-config";
import { AgentOpsService } from "gitpod-next-api/agentops/v1/agentops_pb";
import { AccountService } from "gitpod-next-api/gitpod/v1/account_pb";
import { AgentService } from "gitpod-next-api/gitpod/v1/agent_pb";
import { BillingService } from "gitpod-next-api/gitpod/v1/billing_pb";
import { EditorService } from "gitpod-next-api/gitpod/v1/editor_pb";
import { EnvironmentAutomationService } from "gitpod-next-api/gitpod/v1/environment_automation_pb";
import { EnvironmentService } from "gitpod-next-api/gitpod/v1/environment_pb";
import { EventService } from "gitpod-next-api/gitpod/v1/event_pb";
import { GroupService } from "gitpod-next-api/gitpod/v1/group_pb";
import { InsightsService } from "gitpod-next-api/gitpod/v1/insights_pb";
import { IntegrationService } from "gitpod-next-api/gitpod/v1/integration_pb";
import { NotificationService } from "gitpod-next-api/gitpod/v1/notification_pb";
import { OnaIntelligenceService } from "gitpod-next-api/gitpod/v1/ona_intelligence_pb";
import { OrganizationService } from "gitpod-next-api/gitpod/v1/organization_pb";
import { PrebuildService } from "gitpod-next-api/gitpod/v1/prebuild_pb";
import { ProjectService } from "gitpod-next-api/gitpod/v1/project_pb";
import { RunnerConfigurationService } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { RunnerInteractionService } from "gitpod-next-api/gitpod/v1/runner_interaction_pb";
import { RunnerManagerService } from "gitpod-next-api/gitpod/v1/runner_manager_pb";
import { RunnerService } from "gitpod-next-api/gitpod/v1/runner_pb";
import { SecretService } from "gitpod-next-api/gitpod/v1/secret_pb";
import { ServiceAccountService } from "gitpod-next-api/gitpod/v1/service_account_pb";
import { SessionService } from "gitpod-next-api/gitpod/v1/session_pb";
import { TeamService } from "gitpod-next-api/gitpod/v1/team_pb";
import { UsageService } from "gitpod-next-api/gitpod/v1/usage_pb";
import { UserService } from "gitpod-next-api/gitpod/v1/user_pb";
import { WebhookService } from "gitpod-next-api/gitpod/v1/webhook_pb";
import { WorkflowService } from "gitpod-next-api/gitpod/v1/workflow_pb";

// TODO(at) can only explor the FE's behavior with a different header, as the backend starts validating the header
// but fails as the user cookie is not sent.
const XGitpodPrincipalHeader = "X-Gitpod-Principal";
const principal: Interceptor = (next) => async (req) => {
    const principal = getPrincipal();
    if (principal && !req.header.get(XGitpodPrincipalHeader)) {
        req.header.set(XGitpodPrincipalHeader, "user/" + principal);
    }
    return await next(req);
};

const XGitpodDashboardVersionHeader = "X-Gitpod-Dashboard-Version";
const dashboardVersion: Interceptor = (next) => async (req) => {
    const principal = getPrincipal();
    if (principal && !req.header.get(XGitpodDashboardVersionHeader)) {
        req.header.set(XGitpodDashboardVersionHeader, BuildVersion);
    }
    return await next(req);
};

const XGitpodClientHeader = "X-Gitpod-Client";
const clientType: Interceptor = (next) => async (req) => {
    if (!req.header.get(XGitpodClientHeader)) {
        let client = "web";
        if (window.isWebview) {
            client = "vscode";
        } else if (window.ipcRenderer) {
            client = "desktop";
        }
        req.header.set(XGitpodClientHeader, client);
    }
    return await next(req);
};

const interceptors = [principal, dashboardVersion, clientType];
if (webConfig.accessToken) {
    const token: Interceptor = (next) => async (req) => {
        req.header.set("Authorization", `Bearer ${webConfig.accessToken}`);
        return await next(req);
    };
    interceptors.push(token);
}

const transport = createConnectTransport({
    baseUrl: webConfig.origin + "/api",
    // TODO: add interceptor for csrf token once implemented
    // TODO: should we enable for non-dev environments?
    useBinaryFormat: false,
    interceptors,
    defaultTimeoutMs: 30 * 1000,
});

const accountService = createClient(AccountService, transport);
const userService = createClient(UserService, transport);
const organizationService = createClient(OrganizationService, transport);
const billingService = createClient(BillingService, transport);
const environmentService = createClient(EnvironmentService, transport);
const environmentAutomationService = createClient(EnvironmentAutomationService, transport);
const projectService = createClient(ProjectService, transport);
const secretService = createClient(SecretService, transport);
const runnerInteractionService = createClient(RunnerInteractionService, transport);
const eventService = createClient(EventService, transport);
const editorService = createClient(EditorService, transport);
const runnerService = createClient(RunnerService, transport);
const runnerConfigurationService = createClient(RunnerConfigurationService, transport);
const runnerManagerService = createClient(RunnerManagerService, transport);
const groupService = createClient(GroupService, transport);
const agentService = createClient(AgentService, transport);
const usageService = createClient(UsageService, transport);
const workflowService = createClient(WorkflowService, transport);
const webhookService = createClient(WebhookService, transport);
const serviceAccountService = createClient(ServiceAccountService, transport);
const agentOpsService = createClient(AgentOpsService, transport);
const integrationService = createClient(IntegrationService, transport);
const prebuildService = createClient(PrebuildService, transport);
const onaIntelligenceService = createClient(OnaIntelligenceService, transport);
const insightsService = createClient(InsightsService, transport);
const sessionService = createClient(SessionService, transport);
const teamService = createClient(TeamService, transport);
const notificationService = createClient(NotificationService, transport);
const GitpodAPI = {
    accountService,
    userService,
    organizationService,
    billingService,
    environmentService,
    environmentAutomationService,
    secretService,
    projectService,
    eventService,
    editorService,
    runnerService,
    runnerInteractionService,
    runnerConfigurationService,
    runnerManagerService,
    groupService,
    agentService,
    usageService,
    workflowService,
    webhookService,
    serviceAccountService,
    agentOpsService,
    integrationService,
    prebuildService,
    onaIntelligenceService,
    insightsService,
    sessionService,
    teamService,
    notificationService,
};

type GitpodAPI = typeof GitpodAPI;

export { GitpodAPI };

export function withUser(options: CallOptions, userId: string): CallOptions {
    options.headers = Object.assign(options.headers || {}, {
        [XGitpodPrincipalHeader]: "user/" + userId,
    });
    return options;
}

export type ServiceAccountHeaders = {
    serviceAccountId: string;
    headers: Record<"Authorization", string>;
};

export function serviceAccountHeaders({
    serviceAccountId,
    serviceAccountAccessToken,
}: {
    serviceAccountId: string;
    serviceAccountAccessToken: string;
}): ServiceAccountHeaders {
    return {
        serviceAccountId,
        headers: {
            Authorization: `Bearer ${serviceAccountAccessToken}`,
        },
    };
}
